import { useQuery } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';

import { autosuggestUnbxdV2 } from 'actions/search';
import { Drawer, DrawerBody, DrawerFooter, DrawerHeader, DrawerRoot } from 'components/ui/drawer';
import { Input } from 'components/ui/field';
import { ScrollArea } from 'components/ui/scroll-area';
import { useI18n } from 'context/i18n';
import { useLocale } from 'hooks';
import { useDebounce } from 'hooks/useDebounce';
import { queryKeys } from 'utils/constants/reactQuery';
import { COOKIES, getCookie, getRealLanguage, obtainVoucherPrices, updateQuerystringParam } from 'utils/helpers';
import { ProgressCircle } from 'components/ui/progress-circle';
import { normalizeUnbxdProducts } from 'utils/search';
import { useUser } from 'context/user';
import { ProgressiveImg } from 'components/base';
import { Button } from 'components/ui/button';
import { useRouter } from 'next/router';
import { getNextQueryValue } from 'utils/common';
import { isBlank } from 'utils/StringUtils';
import { useEventListener } from 'hooks/useEventListener';
import { LabCloseIcon } from 'components/camperlab/icons/close';

function calculateHeight() {
  if (typeof window === 'undefined') {
    console.warn('Window is undefined, returning 0 for height calculation');
    return 0;
  }
  // heights of the elements with ids searchHeader, searchInputs and searchFooter
  const searchHeader = document.getElementById('searchHeader')?.clientHeight ?? 0;
  const searchInputs = document.getElementById('searchInputs')?.clientHeight ?? 0;
  const searchFooter = document.getElementById('searchFooter')?.clientHeight ?? 0;
  const searchListTitle = document.getElementById('searchListTitle')?.clientHeight ?? 0;

  const gaps = 130 + 10 + 10;
  return searchHeader + searchInputs + searchFooter + searchListTitle + gaps;
}

export function LabSearchDrawer({ isOpen, setIsOpen }: { isOpen: boolean; setIsOpen: (isOpen: boolean) => void }) {
  const { t, profileData, setSearchQuery } = useI18n();
  const { query } = useRouter();
  const { contextFilters, activatedPrevouchers, handleActivatePrevouchers } = useUser();
  const { country, locale, isoLocale, realLanguage, language } = useLocale();
  const [input, setInput] = useState(getNextQueryValue(query.searchTerm) ?? '');
  const [navigating, setNavigating] = useState(false);
  const searchGridRef = useRef<HTMLDivElement>(null);

  const searchValue = useDebounce(input, 300);
  const { data, isFetching } = useQuery({
    queryKey: [queryKeys.search, realLanguage, searchValue],
    queryFn: async () => {
      const unbxdUid = getCookie(COOKIES.UNBXD_UID) || null;
      const unbxdTranslations = [t('buscador', 'unbxd.api.key'), t('buscador', 'unbxd.site.key')];
      // const unbxdTranslations = [t('buscador', 'unbxd.api.key'), 'ss-unbxd-camper-prod38961673358280'];
      const searchToUse = searchValue === '' ? '*' : searchValue;
      const response = await autosuggestUnbxdV2({ lang: realLanguage, market: country, query: searchToUse, translations: unbxdTranslations, uid: unbxdUid });

      return response;
    },
    placeholderData: (previousData) => previousData,
  });
  // const topQueries =
  //   data?.products?.filter((item) => DOCTYPES_LIST.includes(item.doctype)).map((item) => ({ id: item.autosuggest, label: item.autosuggest })) ?? [];

  const normalizedProducts = normalizeUnbxdProducts(data?.products ?? [], { country, isoLocale, realLanguage, filter: true, square: true });
  const realMarketLang = `${country}_${getRealLanguage(language)}`;

  const getHeightSum = useCallback((el: HTMLDivElement) => {
    if (el) {
      searchGridRef.current = el;
      el.style.setProperty('--height-sum', `${calculateHeight()}px`);
    }
  }, []);

  useEventListener(
    'resize',
    () => {
      if (searchGridRef.current) {
        searchGridRef.current.style.setProperty('--height-sum', `${calculateHeight()}px`);
      }
    },
    undefined,
    { passive: true },
  );

  const onSearch = () => {
    if (!window.location.pathname.includes('/search')) {
      setNavigating(true);
      window.location.assign(`/${locale}/search?searchTerm=${isBlank(searchValue) ? 'Camperlab' : searchValue}`);
    } else {
      setIsOpen(false);
      updateQuerystringParam(window.location.search, 'searchTerm', input);
      setSearchQuery(input);
    }
  };

  return (
    <DrawerRoot isOpen={isOpen} onOpenChange={setIsOpen}>
      <Drawer className="overflow-hidden" aria-label={t('search.widget', 'search', 'search')}>
        <DrawerHeader id="searchHeader" label={t('search.widget', 'search', 'search')} />
        <DrawerBody className="">
          <div className="flex h-full flex-col gap-2.5">
            <div id="searchInputs" className="relative flex flex-col gap-2">
              <Input
                value={input}
                id="searchBox"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    onSearch();
                  }
                }}
                onChange={(e) => {
                  setInput(e.target.value);
                }}
                className="sticky top-0"
                placeholder={t('search.widget', 'input.placeholder')}
              />
              <ProgressCircle
                style={{ opacity: isFetching ? 1 : 0 }}
                aria-hidden
                className="absolute right-2 top-0 flex h-8 items-center transition-opacity duration-150 lg:h-6"
              />
              <Button
                variant="none"
                excludeFromTabOrder
                style={{ opacity: !isFetching && input !== '' ? 1 : 0 }}
                onPress={() => setInput('')}
                className="absolute right-2 top-0 flex h-8 items-center transition-opacity duration-150 lg:h-6"
              >
                <LabCloseIcon />
              </Button>
            </div>
            <div ref={getHeightSum}>
              <h3 id="searchListTitle" className="mb-2.5 text-sm uppercase lg:text-xs">
                {normalizedProducts.length === 0 ? t('search.widget', 'no.products') : t('search.widget', 'best.results')}
              </h3>
              <ScrollArea className="-mr-1 h-[calc(var(--visual-viewport-height)-var(--height-sum))] pr-1">
                <ul className="isolate mb-0 grid h-full grid-cols-2 gap-2">
                  {normalizedProducts.map((item, index) => {
                    const product = { ...item };
                    const voucherPriceInfo = obtainVoucherPrices(
                      item,
                      t,
                      profileData,
                      contextFilters,
                      activatedPrevouchers,
                      handleActivatePrevouchers,
                      false,
                      false,
                      'price.message.short',
                    );

                    let voucherMessage = null;

                    if (voucherPriceInfo !== null) {
                      if (voucherPriceInfo.message) {
                        voucherMessage = voucherPriceInfo.message;
                      } else {
                        product[`${country}_max_sale_price_unx_d`] = parseFloat(voucherPriceInfo?.info?.current);
                        product[`${country}_max_price_unx_d`] = voucherPriceInfo?.info?.previous;
                        product[`${country}_sale_percentage`] = voucherPriceInfo?.info?.discount;
                      }
                    }

                    return (
                      <li
                        key={`unbxd-product-${index}`}
                        className={`group relative flex flex-col`}
                        data-value={product.autosuggest}
                        data-src=""
                        data-index={index}
                        data-type={product.doctype}
                        data-pid={product.uniqueId}
                      >
                        <a className="group block text-sm font-bold uppercase lg:text-xs" href={product.url} aria-label={product.title}>
                          <ProgressiveImg src={product.images.md} className="w-full object-contain" alt="Product Image" />
                          <p className="z-20 mb-0 hover:underline">{product.title}</p>
                          <div className="unbxd-oss-supporttext">{product.description}</div>
                          <div className="unbxd-as-popular-product-price">
                            {product.prices.previousFormated ?
                              <>
                                <div className="unbxd-sale-price !font-bold empty:hidden">{product.prices.currentFormated ?? null}</div>
                                <div>
                                  <span className="unbxd-was-price">{product.prices.previousFormated ?? null}</span>
                                  <span className="unbxd-discount empty:hidden">{product.prices.discount ? `-${product.prices.discount}%` : null}</span>
                                </div>
                              </>
                            : <div className="unbxd-sale-price !font-bold">{product.prices.currentFormated ?? null}</div>}
                            {voucherMessage !== null && <span className="text-black text-[10px] font-medium">{voucherMessage}</span>}
                          </div>
                        </a>
                      </li>
                    );
                  })}
                </ul>
              </ScrollArea>
            </div>
          </div>
        </DrawerBody>
        <DrawerFooter id="searchFooter">
          <Button variant="solid" onPress={onSearch} isPending={navigating}>
            {t('search.widget', 'see.all.results')}
          </Button>
        </DrawerFooter>
      </Drawer>
    </DrawerRoot>
  );
}
